import React, { useEffect, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "sonner";
import { api, fmtRelative } from "../../lib/api";
import { Check, X, Plus, ExternalLink, GitPullRequest, Bell, Server } from "lucide-react";
import { Th, Td, Pill, SectionTitle, Loading, errMsg, usd } from "./shared";

const BLANK_CONN = {
  name: "", platform: "gke", access_mode: "credentials", metrics_source: "cloud_monitoring",
  gcp_project: "", gcp_location: "", aws_region: "", cluster_name: "", prometheus_url: "",
  coverage_target_pct: 95,
};

const STATUS = {
  draft: { label: "Draft", cls: "bg-[hsl(var(--cs-tint))] text-muted-foreground border-border" },
  needs_credentials: { label: "Needs credentials", cls: "bg-[#fef3c7] text-[#92400e] border-[#fde68a]" },
  validated: { label: "Checks passed", cls: "bg-[#e0f2fe] text-[#0369a1] border-[#bae6fd]" },
  connected: { label: "Live", cls: "bg-[#dcfce7] text-[#15803d] border-[#bbf7d0]" },
  error: { label: "Failed", cls: "bg-[#fee2e2] text-[#b91c1c] border-[#fecaca]" },
};

const Field = ({ label, hint, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground mb-1">{label}</div>
    {children}
    {hint && <div className="text-[10px] text-muted-foreground mt-0.5">{hint}</div>}
  </div>
);

const Btn = ({ children, onClick, primary, testId, disabled }) => (
  <button data-testid={testId} onClick={onClick} disabled={disabled}
    className={`px-3 py-1.5 rounded-sm text-xs transition-colors disabled:opacity-50 ${
      primary ? "bg-[#00579e] text-white hover:bg-[#014a87]" : "border border-border hover:bg-[hsl(var(--cs-tint))]"
    }`}>
    {children}
  </button>
);

export const KubeConnect = ({ clusters }) => {
  const [data, setData] = useState(null);
  const [git, setGit] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [prs, setPrs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [manifest, setManifest] = useState(null);
  const [gitForm, setGitForm] = useState(null);

  const load = () => {
    api.kube.connections().then(setData).catch(console.error);
    api.kube.gitopsConfig().then((d) => {
      setGit(d);
      setGitForm((f) => f || d.config || {
        owner: "", repo_name: "", base_branch: "", default_mode: "pr",
        path_template: "clusters/{cluster}/{namespace}/{workload}.yaml",
        branch_prefix: "astra/rightsize", labels: ["astra", "finops"], reviewers: [],
      });
    }).catch(console.error);
    api.kube.alerts().then(setAlerts).catch(console.error);
    api.kube.pullRequests().then((d) => setPrs(d.pull_requests)).catch(console.error);
  };
  useEffect(load, []);

  const act = async (fn, okMsg, after) => {
    try {
      const res = await fn();
      toast.success(okMsg, { description: after?.(res) });
      load();
      return res;
    } catch (e) {
      toast.error("That didn't work", { description: errMsg(e) });
    }
  };

  if (!data || !git || !alerts || !gitForm) return <Loading label="Loading integrations…" testId="kube-connect-loading" />;

  const gitCfg = gitForm;
  const setGitField = (k) => (v) => setGitForm({ ...gitForm, [k]: v });

  return (
    <div className="space-y-6" data-testid="kube-connect">
      {/* ── Clusters ─────────────────────────────────────────────── */}
      <div className="cs-card p-5" data-testid="kube-connections">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <SectionTitle hint="Read-only access either way: credentials call the cloud API, or an in-cluster agent pushes metrics out. Nothing inbound is opened.">
            <span className="inline-flex items-center gap-2"><Server className="w-4 h-4 text-[#00579e]" /> Connected clusters</span>
          </SectionTitle>
          <Btn testId="kube-new-connection" primary onClick={() => setEditing({ ...BLANK_CONN })}>
            <span className="inline-flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Connect a cluster</span>
          </Btn>
        </div>

        {!data.connections.length ? (
          <div className="text-center text-sm text-muted-foreground py-8" data-testid="kube-connections-empty">
            No live clusters yet — the fleet is showing reference data. Connect a GKE or EKS cluster to replace it.
          </div>
        ) : (
          <div className="space-y-3">
            {data.connections.map((c) => (
              <div key={c.id} className="rounded-sm border border-border p-3.5" data-testid={`kube-conn-${c.id}`}>
                <div className="flex items-start gap-3 flex-wrap">
                  <Pill cls={STATUS[c.status]?.cls || STATUS.draft.cls}>{STATUS[c.status]?.label || c.status}</Pill>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold">{c.name}
                      <span className="ml-2 cs-mono text-[11px] text-muted-foreground uppercase">{c.platform}</span>
                    </div>
                    <div className="cs-mono text-[11px] text-muted-foreground mt-0.5">
                      {c.platform === "gke" ? `${c.gcp_project || "—"} · ${c.gcp_location || "—"}` : `${c.aws_region || "—"}`}
                      {" · "}{c.cluster_name} · {c.access_mode === "agent" ? "in-cluster agent" : "read-only credentials"} · metrics: {c.metrics_source.replace("_", " ")}
                    </div>
                    {!!c.cluster_facts && (
                      <div className="cs-mono text-[11px] text-[#15803d] mt-0.5">
                        k8s {c.cluster_facts.k8s_version} · {c.cluster_facts.nodes_total} nodes · {c.cluster_facts.node_pools?.length || 0} pools
                        {c.cluster_facts.in_place_resize ? " · in-place resize supported" : " · rolling restart required"}
                      </div>
                    )}
                    {!!c.live_rows && <div className="text-[11px] text-[#15803d] mt-0.5">{c.live_rows} live workloads · synced {fmtRelative(c.last_sync)}</div>}
                  </div>
                  <div className="ml-auto flex items-center gap-1.5 shrink-0">
                    <Btn testId={`kube-validate-${c.id}`} onClick={() => act(() => api.kube.validateConnection(c.id), "Connection checked", (r) => r.checks?.map((x) => `${x.ok ? "✓" : "✗"} ${x.label}`).join(" · "))}>Run checks</Btn>
                    {c.access_mode === "agent" && (
                      <Btn testId={`kube-manifest-${c.id}`} onClick={async () => setManifest(await api.kube.agentManifest(c.id))}>Agent manifest</Btn>
                    )}
                    {c.status === "connected"
                      ? <Btn testId={`kube-pause-${c.id}`} onClick={() => act(() => api.kube.pauseConnection(c.id), "Paused — back to reference data")}>Pause</Btn>
                      : <Btn testId={`kube-activate-${c.id}`} primary disabled={c.status !== "validated"}
                          onClick={() => act(() => api.kube.activateConnection(c.id), "Cluster is live")}>Go live</Btn>}
                    <Btn testId={`kube-edit-conn-${c.id}`} onClick={() => setEditing({ ...c })}>Edit</Btn>
                    <Btn testId={`kube-delete-conn-${c.id}`} onClick={() => window.confirm(`Remove ${c.name}?`) && act(() => api.kube.deleteConnection(c.id), "Connection removed")}>Remove</Btn>
                  </div>
                </div>
                {!!c.checks?.length && (
                  <div className="flex flex-wrap gap-2 mt-2.5">
                    {c.checks.map((x) => (
                      <span key={x.id} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border text-[11px] ${x.ok ? "border-[#bbf7d0] bg-[#f0fdf4] text-[#15803d]" : "border-[#fecaca] bg-[#fef2f2] text-[#b91c1c]"}`}>
                        {x.ok ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />} {x.label}
                        <span className="text-muted-foreground">· {x.detail}</span>
                      </span>
                    ))}
                  </div>
                )}
                {!!c.missing_credentials?.length && (
                  <div className="text-[11px] text-[#92400e] mt-2">
                    Waiting on {c.missing_credentials.join(" and ")} — ask the builder to add {c.missing_credentials.length > 1 ? "them" : "it"} securely, then run the checks again.
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── GitOps ───────────────────────────────────────────────── */}
      <div className="cs-card p-5" data-testid="kube-gitops">
        <SectionTitle hint="An approved change is written to the manifest repository as a pull request, so the next deploy cannot revert it.">
          <span className="inline-flex items-center gap-2"><GitPullRequest className="w-4 h-4 text-[#6d28d9]" /> GitOps pull requests</span>
        </SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Repository owner"><Input className="h-8 text-xs" data-testid="kube-git-owner" value={gitCfg.owner || ""}
            onChange={(e) => setGitField("owner")(e.target.value)} placeholder="my-org" /></Field>
          <Field label="Repository name"><Input className="h-8 text-xs" data-testid="kube-git-repo" value={gitCfg.repo_name || ""}
            onChange={(e) => setGitField("repo_name")(e.target.value)} placeholder="k8s-manifests" /></Field>
          <Field label="Base branch" hint="Blank uses the repository default."><Input className="h-8 text-xs" data-testid="kube-git-base" value={gitCfg.base_branch || ""}
            onChange={(e) => setGitField("base_branch")(e.target.value)} placeholder="main" /></Field>
          <Field label="Manifest path template" hint="{cluster}, {namespace} and {workload} are substituted.">
            <Input className="h-8 text-xs" data-testid="kube-git-path" value={gitCfg.path_template || ""}
              onChange={(e) => setGitField("path_template")(e.target.value)} /></Field>
          <Field label="Branch prefix"><Input className="h-8 text-xs" data-testid="kube-git-prefix" value={gitCfg.branch_prefix || ""}
            onChange={(e) => setGitField("branch_prefix")(e.target.value)} /></Field>
          <Field label="Default behaviour" hint="Each automation rule can override this.">
            <Select value={gitCfg.default_mode} onValueChange={setGitField("default_mode")}>
              <SelectTrigger className="h-8 text-xs" data-testid="kube-git-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                {git.modes.map((m) => <SelectItem key={m.id} value={m.id} data-testid={`kube-git-mode-${m.id}`}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="PR labels" hint="Comma separated."><Input className="h-8 text-xs" data-testid="kube-git-labels" value={(gitCfg.labels || []).join(", ")}
            onChange={(e) => setGitField("labels")(e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} /></Field>
          <Field label="Reviewers" hint="GitHub usernames, comma separated."><Input className="h-8 text-xs" data-testid="kube-git-reviewers" value={(gitCfg.reviewers || []).join(", ")}
            onChange={(e) => setGitField("reviewers")(e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} /></Field>
        </div>
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <Btn testId="kube-git-save" primary onClick={() => act(() => api.kube.saveGitops(gitCfg), "GitOps settings saved")}>Save settings</Btn>
          <Btn testId="kube-git-verify" onClick={() => act(() => api.kube.verifyGitops(), "Repository reachable", (r) => `base branch ${r.base_branch}`)}>Verify repository access</Btn>
          <span className={`text-[11px] ${git.token_present ? "text-[#15803d]" : "text-[#92400e]"}`} data-testid="kube-git-token-state">
            {git.token_present ? "A GitHub token is stored — use Verify repository access to confirm it works"
                               : "No GitHub token stored yet — pull requests will fail until one is added"}
          </span>
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr><Th>PR</Th><Th>Workload</Th><Th>Cluster</Th><Th>File</Th><Th right>Saving / mo</Th><Th>Opened</Th><Th /></tr></thead>
            <tbody>
              {prs.map((p) => (
                <tr key={p.id} className="border-t border-border" data-testid={`kube-pr-${p.number}`}>
                  <Td mono>#{p.number}</Td>
                  <Td className="font-medium">{p.namespace}/{p.workload}</Td>
                  <Td className="text-muted-foreground">{p.cluster}</Td>
                  <Td mono className="text-muted-foreground">{p.path}</Td>
                  <Td right mono className="text-[#15803d] font-semibold">{usd(p.savings_usd)}</Td>
                  <Td mono className="text-muted-foreground">{fmtRelative(p.created_at)}</Td>
                  <Td right><a href={p.url} target="_blank" rel="noreferrer" className="text-[#00579e] hover:underline inline-flex items-center gap-1">open <ExternalLink className="w-3 h-3" /></a></Td>
                </tr>
              ))}
              {!prs.length && (
                <tr><td colSpan={7} className="text-center text-muted-foreground py-6" data-testid="kube-pr-empty">
                  No pull requests yet — approve a right-sizing recommendation with pull-request mode on.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Slack ────────────────────────────────────────────────── */}
      <div className="cs-card p-5" data-testid="kube-alerts">
        <SectionTitle hint="Alerts are always recorded here, so you can see what would have been sent even before Slack is wired up.">
          <span className="inline-flex items-center gap-2"><Bell className="w-4 h-4 text-[#be185d]" /> Slack alerts · {alerts.settings.channel_label}</span>
        </SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            {alerts.types.map((t) => (
              <div key={t.id} className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-xs">{t.label}</div>
                  {t.note && <div className="text-[10px] text-muted-foreground">{t.note}</div>}
                </div>
                <Switch data-testid={`kube-alert-toggle-${t.id}`} checked={!!alerts.settings.enabled[t.id]}
                  onCheckedChange={(v) => setAlerts({ ...alerts, settings: { ...alerts.settings, enabled: { ...alerts.settings.enabled, [t.id]: v } } })} />
              </div>
            ))}
          </div>
          <div className="space-y-3">
            <Field label="Coverage target %" hint="Below this, a cluster raises a coverage alert.">
              <Input type="number" className="h-8 text-xs" data-testid="kube-alert-coverage" value={alerts.settings.coverage_target_pct}
                onChange={(e) => setAlerts({ ...alerts, settings: { ...alerts.settings, coverage_target_pct: Number(e.target.value) } })} /></Field>
            <Field label="Digest hour (UTC)">
              <Input type="number" min="0" max="23" className="h-8 text-xs" data-testid="kube-alert-hour" value={alerts.settings.digest_hour_utc}
                onChange={(e) => setAlerts({ ...alerts, settings: { ...alerts.settings, digest_hour_utc: Number(e.target.value) } })} /></Field>
            <Field label="Channel label" hint="Shown in the app; the webhook decides the real channel.">
              <Input className="h-8 text-xs" data-testid="kube-alert-channel" value={alerts.settings.channel_label}
                onChange={(e) => setAlerts({ ...alerts, settings: { ...alerts.settings, channel_label: e.target.value } })} /></Field>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <Btn testId="kube-alert-save" primary onClick={() => act(() => api.kube.saveAlerts({
            enabled: alerts.settings.enabled, coverage_target_pct: alerts.settings.coverage_target_pct,
            digest_hour_utc: alerts.settings.digest_hour_utc, channel_label: alerts.settings.channel_label,
          }), "Alert settings saved")}>Save settings</Btn>
          <Btn testId="kube-alert-test" onClick={() => act(() => api.kube.testAlert(), "Test alert processed", (r) => r.sent ? "Delivered to Slack" : r.reason)}>Send test alert</Btn>
          <Btn testId="kube-alert-checks" onClick={() => act(() => api.kube.runAlertChecks(), "Checks run", (r) => `${r.coverage_alerts} coverage · ${r.stale_agent_alerts} stale agent`)}>Run checks now</Btn>
          <Btn testId="kube-alert-digest" onClick={() => act(() => api.kube.sendDigest(), "Digest processed", (r) => r.sent ? "Delivered to Slack" : r.reason)}>Send digest now</Btn>
          <span className={`text-[11px] ${alerts.webhook_present ? "text-[#15803d]" : "text-[#92400e]"}`} data-testid="kube-alert-webhook-state">
            {alerts.webhook_present ? "Slack webhook is stored" : "No Slack webhook stored yet — alerts are recorded but not delivered"}
          </span>
        </div>

        <div className="mt-4 max-h-[280px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-white"><tr><Th>When</Th><Th>Type</Th><Th>Alert</Th><Th>Delivery</Th></tr></thead>
            <tbody>
              {alerts.outbox.map((a) => (
                <tr key={a.id} className="border-t border-border align-top" data-testid={`kube-outbox-${a.id}`}>
                  <Td mono className="text-muted-foreground whitespace-nowrap">{fmtRelative(a.created_at)}</Td>
                  <Td mono>{a.type}</Td>
                  <Td className="max-w-[420px]">{a.title}
                    <div className="text-[10px] text-muted-foreground">{(a.lines || []).join(" ").replace(/\*/g, "")}</div></Td>
                  <Td className={a.delivered ? "text-[#15803d]" : "text-[#92400e]"}>{a.delivered ? "delivered" : a.error || "not delivered"}</Td>
                </tr>
              ))}
              {!alerts.outbox.length && (
                <tr><td colSpan={4} className="text-center text-muted-foreground py-6" data-testid="kube-outbox-empty">No alerts yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── connection dialog ────────────────────────────────────── */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="kube-conn-dialog">
          {editing && (
            <>
              <DialogHeader><DialogTitle className="cs-heading text-base">Connect a cluster</DialogTitle></DialogHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Display name"><Input className="h-8 text-xs" data-testid="kube-conn-name" value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="prod-apps" /></Field>
                <Field label="Platform">
                  <Select value={editing.platform} onValueChange={(v) => setEditing({ ...editing, platform: v })}>
                    <SelectTrigger className="h-8 text-xs" data-testid="kube-conn-platform"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gke" data-testid="kube-conn-platform-gke">GKE · Google Cloud</SelectItem>
                      <SelectItem value="eks" data-testid="kube-conn-platform-eks">EKS · AWS</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Access mode" hint="Credentials call the cloud API; the agent pushes metrics out from inside the cluster.">
                  <Select value={editing.access_mode} onValueChange={(v) => setEditing({ ...editing, access_mode: v })}>
                    <SelectTrigger className="h-8 text-xs" data-testid="kube-conn-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="credentials" data-testid="kube-conn-mode-credentials">Read-only credentials</SelectItem>
                      <SelectItem value="agent" data-testid="kube-conn-mode-agent">In-cluster agent</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Metrics source">
                  <Select value={editing.metrics_source} onValueChange={(v) => setEditing({ ...editing, metrics_source: v })}>
                    <SelectTrigger className="h-8 text-xs" data-testid="kube-conn-metrics"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {data.metrics_sources.map((m) => (
                        <SelectItem key={m.id} value={m.id} data-testid={`kube-conn-metrics-${m.id}`}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Cluster name"><Input className="h-8 text-xs" data-testid="kube-conn-cluster" value={editing.cluster_name}
                  onChange={(e) => setEditing({ ...editing, cluster_name: e.target.value })} /></Field>
                {editing.platform === "gke" ? (
                  <>
                    <Field label="GCP project"><Input className="h-8 text-xs" data-testid="kube-conn-project" value={editing.gcp_project || ""}
                      onChange={(e) => setEditing({ ...editing, gcp_project: e.target.value })} /></Field>
                    <Field label="Location" hint="Region or zone, e.g. us-central1"><Input className="h-8 text-xs" data-testid="kube-conn-location" value={editing.gcp_location || ""}
                      onChange={(e) => setEditing({ ...editing, gcp_location: e.target.value })} /></Field>
                  </>
                ) : (
                  <Field label="AWS region"><Input className="h-8 text-xs" data-testid="kube-conn-region" value={editing.aws_region || ""}
                    onChange={(e) => setEditing({ ...editing, aws_region: e.target.value })} /></Field>
                )}
                {editing.metrics_source === "prometheus" && (
                  <Field label="Prometheus URL"><Input className="h-8 text-xs" data-testid="kube-conn-prom" value={editing.prometheus_url || ""}
                    onChange={(e) => setEditing({ ...editing, prometheus_url: e.target.value })} placeholder="https://prom.internal" /></Field>
                )}
                <Field label="Replaces demo cluster" hint="The live data takes over this cluster's place in the fleet.">
                  <Select value={editing.replaces_cluster_id || "none"} onValueChange={(v) => setEditing({ ...editing, replaces_cluster_id: v === "none" ? null : v })}>
                    <SelectTrigger className="h-8 text-xs" data-testid="kube-conn-replaces"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Add as a new cluster</SelectItem>
                      {(clusters || []).map((c) => (
                        <SelectItem key={c.id} value={c.id} data-testid={`kube-conn-replaces-${c.name}`}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <div className="text-[11px] text-muted-foreground">
                {editing.access_mode === "credentials"
                  ? `Needs ${editing.platform === "gke" ? "a read-only service account key (roles/container.viewer)" : "read-only access keys (eks:DescribeCluster, eks:ListNodegroups)"} — credentials are requested securely, never typed here.`
                  : "You'll get a manifest to apply after saving. The agent only pushes out; nothing inbound is opened."}
              </div>
              <div className="flex items-center gap-2">
                <Btn testId="kube-conn-save" primary onClick={async () => {
                  const saved = await act(() => api.kube.saveConnection(editing), "Connection saved");
                  if (saved) setEditing(null);
                }}>Save</Btn>
                <Btn onClick={() => setEditing(null)}>Cancel</Btn>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!manifest} onOpenChange={(o) => !o && setManifest(null)}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="kube-manifest-dialog">
          {manifest && (
            <>
              <DialogHeader><DialogTitle className="cs-heading text-base">In-cluster agent manifest</DialogTitle></DialogHeader>
              <p className="text-xs text-muted-foreground">{manifest.note}</p>
              <pre className="p-3 rounded-sm bg-[#0f172a] text-[#e2e8f0] cs-mono text-[11px] overflow-x-auto max-h-[420px]">{manifest.manifest}</pre>
              <div className="flex items-center gap-2">
                <Btn testId="kube-manifest-copy" primary onClick={() => {
                  navigator.clipboard?.writeText(manifest.manifest);
                  toast.success("Manifest copied", { description: manifest.apply_command });
                }}>Copy manifest</Btn>
                <span className="cs-mono text-[11px] text-muted-foreground">{manifest.apply_command}</span>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KubeConnect;
